import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import ImageCarousel from '../components/ImageCarousel';
import LoadingScreen from '../components/LoadingScreen';
import { CartContext } from '../contexts/CartContext';
import './ProductDetail.css';

const apiUrl = process.env.REACT_APP_API_URL || 'https://www.nualily.es';

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRating, setUserRating] = useState(0);
  const [isRated, setIsRated] = useState(false);

  const isAdmin = localStorage.getItem('isAdmin') === 'true';
  const { addToCart } = useContext(CartContext);
  const [quantity, setQuantity] = useState(1);

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value);
    if (isNaN(value)) {
      setQuantity(1);
    } else {
      setQuantity(Math.max(1, Math.min(value, product.stock)));
    }
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/products/${id}`);
        setProduct(response.data);
      } catch (error) {
        console.error('Error fetching product:', error);
        setError('Error al cargar el producto. Por favor, intenta más tarde.');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();

    const ratedSession = sessionStorage.getItem(`rated_${id}`);
    if (ratedSession) {
      setIsRated(true);
    }
  }, [id]);

  const handleRating = async (rating) => {
    if (isRated) return;

    try {
      const response = await axios.post(`${apiUrl}/api/products/${id}/rate`, { rating });
      setProduct((prev) => ({
        ...prev,
        averageRating: response.data.averageRating,
      }));
      setUserRating(rating);
      setIsRated(true);
      sessionStorage.setItem(`rated_${id}`, true);
    } catch (error) {
      setError('Error al guardar la valoración.');
    }
  };

  if (loading) return <LoadingScreen />;
  if (error) return <p className="error-message">{error}</p>;

  if (!product || !product.name) {
    return <p className="not-found-message">Producto no encontrado.</p>;
  }

  let stockStatusClass = '';
  let stockStatusLabel = '';
  if (product.stock === 0) {
    stockStatusClass = 'out-of-stock';
    stockStatusLabel = 'Agotado';
  } else if (product.stock < 5) {
    stockStatusClass = 'low-stock';
    stockStatusLabel = '¡Últimas unidades!';
  }

  const formattedPrice = new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
  }).format(product.price);

  const averageRating = product.averageRating ? product.averageRating.toFixed(2) : '0.00';

  return (
    <div className="product-detail-container main-content">
      <div className="product-header">
        <h1 className="product-name">{product.name}</h1>
        {isAdmin && product.isNew && (
          <span className="product-badge new-product">Nuevo Producto</span>
        )}
        {stockStatusLabel && (
          <span className={`product-badge ${stockStatusClass}`}>
            {stockStatusLabel}
          </span>
        )}
      </div>

      {/* Cambio de images a media en el ImageCarousel */}
      <ImageCarousel media={product.media} />
      <p className="product-description">{product.description}</p>
      {product.category && product.category.name && (
        <p className="product-category">{product.category.name}</p>
      )}
      <p className={`product-stock ${stockStatusClass}`}>Stock: {product.stock}</p>
      <p className="product-price">Precio: {formattedPrice}</p>

      <p className="product-ratings">Calificación promedio: {averageRating}</p>

      <div className="rating-stars">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`star ${star <= (userRating || Math.ceil(averageRating)) ? 'filled' : ''}`}
            onClick={() => handleRating(star)}
            style={{ cursor: isRated ? 'default' : 'pointer' }}
          >
            ★
          </span>
        ))}
      </div>

      {product.stock > 0 && (
        <div className="quantity-selector">
          <label htmlFor={`quantity-${product._id}`}>Cantidad:</label>
          <input
            type="number"
            id={`quantity-${product._id}`}
            min="1"
            max={product.stock}
            value={quantity}
            onChange={handleQuantityChange}
          />
        </div>
      )}

      <button
        className="add-to-cart-button"
        onClick={() => addToCart(product, quantity)}
        disabled={product.stock === 0}
      >
        {product.stock === 0 ? 'Agotado' : 'Agregar al Carrito'}
      </button>

      {product.video && (
        <div className="product-video">
          <video controls>
            <source src={product.video} type="video/mp4" />
            Tu navegador no soporta el video.
          </video>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
